// Google Analytics integration utilities
declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}

const GA_MEASUREMENT_ID = 'G-HQEMY6GD53';

// Initialize Google Analytics
export const initializeGA = (): void => {
    // Initialize dataLayer and gtag function directly (CSP-compliant, no inline script)
    window.dataLayer = window.dataLayer || [];
    window.gtag = function(...args: any[]) {
        window.dataLayer.push(args);
    };
    
    // Load the Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag with timestamp and config
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
        page_title: document.title,
        page_location: window.location.href
    });

    console.log('🔍 Google Analytics initialized with ID:', GA_MEASUREMENT_ID);
};

// Track page views
export const trackPageView = (page_title?: string, page_location?: string): void => {
    if (typeof window.gtag !== 'undefined') {
        window.gtag('config', GA_MEASUREMENT_ID, {
            page_title: page_title || document.title,
            page_location: page_location || window.location.href,
        });
    }
};

// Track custom events
export const trackEvent = (action: string, category: string, label?: string, value?: number): void => {
    if (typeof window.gtag !== 'undefined') {
        window.gtag('event', action, {
            event_category: category,
            event_label: label,
            value: value,
        });
    }
};

// Track search events
export const trackSearch = (searchTerm: string, radius: number, filters: {
    residential_needs: boolean;
    dementia_needs: boolean;
    nursing_needs: boolean;
    sort: 'score' | 'fee_low' | 'fee_high' | 'event_date';
}): void => {
    trackEvent('search', 'care_home_search', searchTerm, radius);
    
    // Track filter usage
    if (filters.residential_needs) {
        trackEvent('filter_used', 'search_filters', 'residential_care');
    }
    if (filters.dementia_needs) {
        trackEvent('filter_used', 'search_filters', 'dementia_care');
    }
    if (filters.nursing_needs) {
        trackEvent('filter_used', 'search_filters', 'nursing_care');
    }
    
    // Track sort preference
    trackEvent('sort_used', 'search_options', filters.sort);
};

// Track care home selection
export const trackCareHomeSelection = (careHomeName: string, selectionSource: 'search_card' | 'map_pin'): void => {
    trackEvent('care_home_selected', 'user_interaction', `${selectionSource}_${careHomeName}`);
};

// Track external link clicks
export const trackExternalLink = (url: string, linkType: 'details' | 'contact' | 'cqc'): void => {
    trackEvent('external_link_click', 'navigation', `${linkType}_${url}`);
};

// Set user properties (when consent is given)
export const setUserProperties = (userId: string): void => {
    if (typeof window.gtag !== 'undefined') {
        window.gtag('config', GA_MEASUREMENT_ID, {
            user_id: userId,
            custom_map: {
                'custom_parameter_1': 'osmap_user'
            }
        });
    }
};

// Disable Google Analytics (when consent is rejected)
export const disableGA = (): void => {
    // Set the Google Analytics disable flag
    (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    console.log('🔍 Google Analytics disabled due to cookie rejection');
};
